import type { TFunction } from 'i18next';
import type { Survey } from '../../api/queries';
import type { QuestionnaireQuestion } from '../../api/workEnvironmentTypes';
import type { FormSubmission } from '../../hooks';
import type { Answers } from './workEnvironmentFormView';

type SurveyQuestion = Survey['questions'][number];

/** The template may leave it unset, which means the score is kept as answered */
const isReverseScored = (question: SurveyQuestion) => question.shouldReverseScore === true;

/** The question in the shape the work environment question card shows, with its explanation as help */
export const toQuestionCard = (question: SurveyQuestion): QuestionnaireQuestion => ({
  ref: String(question.id),
  text: question.question,
  help: question.explanation ?? null,
  highIsGood: !isReverseScored(question),
});

export const missingQuestions = (survey: Survey, answers: Answers) =>
  survey.questions.map(toQuestionCard).filter((question) => answers[question.ref] === undefined);

export const answeredCount = (survey: Survey, answers: Answers) =>
  survey.questions.length - missingQuestions(survey, answers).length;

export const surveyResponse = (survey: Survey, answers: Answers): FormSubmission => ({
  surveyId: survey.id,
  answers: survey.questions.map((question) => ({
    questionId: question.id,
    answer: String(answers[String(question.id)]),
    shouldReverseScore: isReverseScored(question),
  })),
});

const SCALE_KEYS = ['never', 'rarely', 'sometimes', 'often', 'veryOften'] as const;

/** Psychological safety asks how often, from never to very often, in the language of the app */
export const scaleLabels = (t: TFunction) =>
  SCALE_KEYS.map((key) => t(`surveys.workEnvironmentSurveyForm.scaleLabels.${key}`));
